import { __publicField } from './chunk-NSSMTXJJ.mjs';
import { z } from 'zod';

// src/shared/errors/mappers/authentication-error.ts
var toAuthenticationError = (error, context) => {
  if (error instanceof AuthenticationError) {
    return { mapped: true, error: new AuthenticationError(`${context}\u4E2D\u306B\u8A8D\u8A3C\u30A8\u30E9\u30FC\u304C\u767A\u751F\u3057\u307E\u3057\u305F`, { cause: error }) };
  }
  return { mapped: false };
};

// src/shared/errors/mappers/firebase-error.ts
var toFirebaseError = (error, context, _tid) => {
  if (error !== null && error !== void 0 && typeof error === "object" && "code" in error && typeof error.code === "string") {
    if (error.code === "permission-denied" || error.code === "unauthenticated") {
      return { mapped: true, error: new AuthenticationError(`${context}\u4E2D\u306BFirebase\u8A8D\u8A3C\u30A8\u30E9\u30FC\u304C\u767A\u751F\u3057\u307E\u3057\u305F: ${error.code}`, { cause: error }) };
    }
    if (error.code === "not-found") {
      return { mapped: true, error: new NotFoundError(`${context}\u4E2D\u306B\u30C7\u30FC\u30BF\u304C\u898B\u3064\u304B\u308A\u307E\u305B\u3093\u3067\u3057\u305F`, { cause: error }) };
    }
    return { mapped: true, error: new UnknownError(`${context}\u4E2D\u306BFirebase\u30A8\u30E9\u30FC\u304C\u767A\u751F\u3057\u307E\u3057\u305F: ${error.code}`, { cause: error }) };
  }
  return { mapped: false };
};

// src/shared/errors/mappers/not-found-error.ts
var toNotFoundError = (error, context) => {
  if (error instanceof NotFoundError) {
    return { mapped: true, error: new NotFoundError(`${context}\u4E2D\u306B\u53D6\u5F15\u304C\u898B\u3064\u304B\u308A\u307E\u305B\u3093\u3067\u3057\u305F`, { cause: error }) };
  }
  return { mapped: false };
};

// src/shared/errors/mappers/timeout-error.ts
var toTimeoutError = (error) => {
  if (error instanceof TimeoutError) {
    return { mapped: true, error };
  }
  return { mapped: false };
};

// src/shared/errors/mappers/unexpected-error.ts
var toUnexpectedError = (error, context, tid) => {
  var _a, _b;
  console.error(`[${context} - Unexpected Error]`, {
    transactionId: tid,
    errorType: (_b = (_a = error == null ? void 0 : error.constructor) == null ? void 0 : _a.name) != null ? _b : typeof error,
    errorMessage: error instanceof Error ? error.message : String(error),
    errorStack: error instanceof Error ? error.stack : void 0,
    timestamp: (/* @__PURE__ */ new Date()).toISOString()
  });
  return { mapped: true, error: new UnknownError(`${context}\u4E2D\u306B\u4E88\u671F\u3057\u306A\u3044\u30A8\u30E9\u30FC\u304C\u767A\u751F\u3057\u307E\u3057\u305F`, { cause: error }) };
};

// src/shared/errors/mappers/unknown-error.ts
var toUnknownError = (error, context) => {
  if (error instanceof UnknownError) {
    return { mapped: true, error: new UnknownError(`${context}\u4E2D\u306B\u30A8\u30E9\u30FC\u304C\u767A\u751F\u3057\u307E\u3057\u305F`, { cause: error }) };
  }
  return { mapped: false };
};
var toZodError = (error, context, tid) => {
  if (error instanceof z.ZodError) {
    console.error(`[${context} - Validation Error]`, {
      transactionId: tid,
      validationErrors: error.flatten(),
      timestamp: (/* @__PURE__ */ new Date()).toISOString()
    });
    return { mapped: true, error: new UnknownError("\u53D6\u5F15\u30C7\u30FC\u30BF\u306E\u691C\u8A3C\u306B\u5931\u6557\u3057\u307E\u3057\u305F", { cause: error }) };
  }
  return { mapped: false };
};

// src/shared/errors/map-custom-error.ts
var createPipeline = (...mappers) => (error, context, tid) => {
  for (const mapper of mappers) {
    const result = mapper(error, context, tid);
    if (result.mapped) {
      throw result.error;
    }
  }
  throw new UnknownError(`${context}\u4E2D\u306B\u4E88\u671F\u3057\u306A\u3044\u30A8\u30E9\u30FC\u304C\u767A\u751F\u3057\u307E\u3057\u305F`, { cause: error });
};
var mapCustomError = createPipeline(toTimeoutError, toNotFoundError, toAuthenticationError, toUnknownError, toFirebaseError, toZodError, toUnexpectedError);

// src/shared/errors/index.ts
var isEmpty = (value) => typeof value === "object" && value !== null;
var BaseError = class extends Error {
  get type() {
    return ["base-error"];
  }
  static is(error) {
    return error instanceof this || isEmpty(error) && "type" in error && Array.isArray(error.type) && error.type.includes("base-error");
  }
};
var NotFoundError = class extends BaseError {
  constructor(message, option) {
    super(`NotFoundError:${message}`, option);
  }
  get type() {
    return [...super.type, "not-found-error"];
  }
  static is(error) {
    return error instanceof this || isEmpty(error) && "type" in error && Array.isArray(error.type) && error.type.includes("not-found-error");
  }
};
var AuthenticationError = class extends BaseError {
  constructor(message, option) {
    super(`AuthenticationError:${message}`, option);
  }
  get type() {
    return [...super.type, "authentication-error"];
  }
  static is(error) {
    return error instanceof this || isEmpty(error) && "type" in error && Array.isArray(error.type) && error.type.includes("authentication-error");
  }
};
var PermissionDeniedError = class extends BaseError {
  constructor(message, option) {
    super(`PermissionDeniedError:${message}`, option);
  }
  get type() {
    return [...super.type, "permission-denied-error"];
  }
  static is(error) {
    return error instanceof this || isEmpty(error) && "type" in error && Array.isArray(error.type) && error.type.includes("permission-denied-error");
  }
};
var UserDisabledError = class extends BaseError {
  constructor(message, option) {
    super(`UserDisabledError:${message}`, option);
  }
  get type() {
    return [...super.type, "user-disabled-error"];
  }
  static is(error) {
    return error instanceof this || isEmpty(error) && "type" in error && Array.isArray(error.type) && error.type.includes("user-disabled-error");
  }
};
var TooManyRequestsError = class extends BaseError {
  constructor(message, option) {
    super(`TooManyRequestsError:${message}`, option);
  }
  get type() {
    return [...super.type, "too-many-requests-error"];
  }
  static is(error) {
    return error instanceof this || isEmpty(error) && "type" in error && Array.isArray(error.type) && error.type.includes("too-many-requests-error");
  }
};
var UnknownError = class extends BaseError {
  constructor(message, option) {
    super(`UnknownError:${message}`, option);
  }
  get type() {
    return [...super.type, "unknown-error"];
  }
  static is(error) {
    return error instanceof this || isEmpty(error) && "type" in error && Array.isArray(error.type) && error.type.includes("unknown-error");
  }
};
var StorageError = class extends BaseError {
  constructor(message, option) {
    super(`StorageError:${message}`, option);
  }
  get type() {
    return [...super.type, "storage-error"];
  }
  static is(error) {
    return error instanceof this || isEmpty(error) && "type" in error && Array.isArray(error.type) && error.type.includes("storage-error");
  }
};
var ImageSizeExceededError = class extends BaseError {
  constructor(fileName, actualSizeMB, maxSizeMB, option) {
    super(`ImageSizeExceededError: ${fileName} - \u753B\u50CF\u304C\u5927\u304D\u3059\u304E\u307E\u3059\uFF08${actualSizeMB.toFixed(2)}MB / \u6700\u5927${maxSizeMB}MB\uFF09`, option);
    __publicField(this, "fileName", fileName);
    __publicField(this, "actualSizeMB", actualSizeMB);
    __publicField(this, "maxSizeMB", maxSizeMB);
  }
  get type() {
    return [...super.type, "image-size-exceeded-error"];
  }
  static is(error) {
    return error instanceof this || isEmpty(error) && "type" in error && Array.isArray(error.type) && error.type.includes("image-size-exceeded-error");
  }
};
var TransactionLimitExceededError = class extends BaseError {
  constructor(message, option) {
    super(`TransactionLimitExceededError:${message}`, option);
  }
  get type() {
    return [...super.type, "transaction-limit-exceeded-error"];
  }
  static is(error) {
    return error instanceof this || isEmpty(error) && "type" in error && Array.isArray(error.type) && error.type.includes("transaction-limit-exceeded-error");
  }
};
var TimeoutError = class extends BaseError {
  constructor(message, option) {
    super(`TimeoutError:${message}`, option);
  }
  get type() {
    return [...super.type, "timeout-error"];
  }
  static is(error) {
    return error instanceof this || isEmpty(error) && "type" in error && Array.isArray(error.type) && error.type.includes("timeout-error");
  }
};
var PartialUpdateError = class extends BaseError {
  constructor(authUpdated, firestoreUpdated, message, option) {
    super(`PartialUpdateError:${message}`, option);
    __publicField(this, "authUpdated", authUpdated);
    __publicField(this, "firestoreUpdated", firestoreUpdated);
  }
  get type() {
    return [...super.type, "partial-update-error"];
  }
  static is(error) {
    return error instanceof this || isEmpty(error) && "type" in error && Array.isArray(error.type) && error.type.includes("partial-update-error");
  }
};
var formatError = (e) => {
  return {
    message: e.message,
    ...e.cause instanceof Error ? { cause: formatError(e.cause) } : {}
  };
};

export { AuthenticationError, BaseError, ImageSizeExceededError, NotFoundError, PartialUpdateError, PermissionDeniedError, StorageError, TimeoutError, TooManyRequestsError, TransactionLimitExceededError, UnknownError, UserDisabledError, formatError, isEmpty, mapCustomError };
//# sourceMappingURL=chunk-DGS6LEDH.mjs.map
//# sourceMappingURL=chunk-DGS6LEDH.mjs.map